import { AlertTriangle, Calendar, Clock } from 'react-feather';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import {
  ExportFormat, Gender, GetRegistrationsQuery as GetRegistrations, Permission, PurchasePaymentStatus, PurchaseStatus,
  RegistrationFilters as RegistrationFiltersType,
  RegistrationStatus,
} from '__generated__/graphql';

import { ExportsContext } from '../../Exports/ExportsProvider';
import { TicketLabel } from '../../../common/Common/SellableLabel';
import { assetUrl, encodeUrl, isEmpty, isGraphQLValidationError } from '../../../common/helpers';
import ExportRegistrationsForm from '../ExportRegistrationsForm';
import GetRegistrationsQuery from './GetRegistrationsQuery';
import NoEventsCard from '../NoEventsCard';
import ProtectedSection from '../../Common/ProtectedSection';
import RegistrationFilters, { PurchaseStatsData, RegistrationFilterParams } from './RegistrationFilters';
import UI from '../../../common/UI';
import config from '../../config';
import useAddOrderModal from '../Participant/Orders/useAddOrderModal';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';
import useUrlState from '../../../common/useUrlState';

type Registration = GetRegistrations['project']['registrations']['data'][0];

interface UrlState extends RegistrationFilterParams {
  page?: number;
}

const RegistrationsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const device = useMediaDevice();
  const project = useProject();

  useDocumentTitle(t('registrations'));
  const recentEvent = useRecentEventId();

  const [filtersKey, setFiltersKey] = useState(0);

  // All filters are stored in the URL so that they can easily be shared
  const [{ page, ...filters }, setUrlState] = useUrlState<UrlState>({
    parse: (rawParams, { string, integer, boolean, array }) => ({
      ...rawParams,
      event: string(rawParams.event),
      search: string(rawParams.search),
      min_age: integer(rawParams.min_age),
      max_age: integer(rawParams.max_age),
      genders: array(rawParams.genders, (value) => (string(value) in Gender ? value : null)),
      locales: array(rawParams.locales, string),
      mail_opt_ins: array(rawParams.mail_opt_ins, string),
      ticket_categories: array(rawParams.ticket_categories, string),
      tickets: array(rawParams.tickets, string),
      time_slots: array(rawParams.time_slots, string),
      products: array(rawParams.products, string),
      product_variants: array(rawParams.product_variants, string),
      promotions: array(rawParams.promotions, string),
      invitations: array(rawParams.invitations, string),
      coupons: array(rawParams.coupons, string),
      lotteries: array(rawParams.lotteries, string),
      confirmed_lotteries: array(rawParams.confirmed_lotteries, string),
      rejected_lotteries: array(rawParams.rejected_lotteries, string),
      statuses: array(rawParams.statuses, (value) => (string(value) in PurchaseStatus ? value : null)),
      payment_statuses: array(
        rawParams.payment_statuses,
        (value) => (string(value) in PurchasePaymentStatus ? value : null),
      ),
      participant_fields: array(rawParams.participant_fields, (value) => (typeof value === 'object' ? value : null)),
      participant_field_choices: array(rawParams.participant_field_choices, string),
      ticket_number_sequences: array(rawParams.ticket_number_sequences, string),
      has_ticket_number: boolean(rawParams.has_ticket_number),
      has_team: boolean(rawParams.has_team),
      page: integer(rawParams.page, 1),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Registrations' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  // State that contains all filter stats
  const [filterStats, setFilterStats] = useState<PurchaseStatsData>();

  const registrationsCount = filterStats?.tickets || 0;
  const enableExport = registrationsCount > 0 && registrationsCount <= config.maxExportSize.registrations;

  /** Replaces filters in the URL */
  const handleFiltersChange = (update: (filters: RegistrationFilterParams) => RegistrationFilterParams) => {
    setUrlState(({ page, ...urlState }) => update({ ...urlState, ...filters }), true);
  };

  const handleStatsChange = (stats: PurchaseStatsData) => {
    setFilterStats(stats);
  };

  /** Filters object that can be sent to the server */
  const registrationFilters: RegistrationFiltersType = {
    ...omit(filters, ['event', 'locales', 'mail_opt_ins']),
    events: filters.event ? [filters.event] : undefined,
    participant_filters: {
      locales: filters.locales,
      mail_opt_ins: filters.mail_opt_ins,
    },
  };

  // Fetches the filtered participants
  const { data, loading } = useQuery(GetRegistrationsQuery, {
    variables: {
      ...registrationFilters,
      project_id: project.id,
      page,
      per_page: 15,
    },
    onError: (error) => {
      if (isGraphQLValidationError(error)) {
        // Recent event no longer exists, reset it.
        recentEvent.reset();
      }
    },
  });

  const events = data?.project.events;
  const registrations = data?.project.registrations;

  const [showEvent, setShowEvent] = useState(!filters.event);
  const registrationsRef = useRef(registrations);

  // Show event labels after new registrations have been retrieved.
  useEffect(() => {
    if (registrationsRef.current !== registrations) {
      setShowEvent(!filters.event);
    }
    registrationsRef.current = registrations;
  }, [registrations, filters.event]);

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((filter) => !isEmpty(filter));

  const [openAddRegistrationModal] = useAddOrderModal({
    eventId: filters.event,
    onSuccess: (order) => {
      history.push(`/${project.id}/participants/${order.participant.id}/orders/${order.id}`);
    },
  });

  const { openExportsModal } = useContext(ExportsContext);

  const [openExportRegistrationsModal, closeExportRegistrationsModal] = useModal(() => (
    <UI.Modal maxWidth={800} close={closeExportRegistrationsModal}>
      <ExportRegistrationsForm
        filters={registrationFilters}
        registrationsCount={registrationsCount}
        onSuccess={({ format }) => {
          closeExportRegistrationsModal();
          openExportsModal(format === ExportFormat.mailchimp ? 'mailchimp' : 'recent_exports');
        }}
      />
    </UI.Modal>
  ), [filters, registrationsCount, openExportsModal]);

  if (!events) {
    return <UI.PageLoader />;
  }

  if (events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.GridContainer sc={{ columns: ['2fr 3fr'], alignVertical: 'center' }}>
          <UI.Div>
            <UI.SectionHeading>
              {t('registrations')}
            </UI.SectionHeading>
          </UI.Div>
          <UI.FlexContainer sc={{ justifyContent: 'end' }}>
            <ProtectedSection permissions={[Permission.edit_registrations]}>
              <UI.Button
                onClick={openAddRegistrationModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_registration')}
              </UI.Button>
            </ProtectedSection>
            {!device.isSmall && (
              <ProtectedSection permissions={[Permission.exports]}>
                <UI.TooltipContainer sc={{ ml: 2 }}>
                  {registrationsCount > config.maxExportSize.registrations && (
                    <UI.Tooltip sc={{ alignH: 'center', alignV: 'bottom' }}>
                      <UI.Icon>
                        <AlertTriangle />
                      </UI.Icon>
                      {' '}
                      {t('export_too_large')}
                    </UI.Tooltip>
                  )}
                  <UI.Button
                    onClick={() => openExportRegistrationsModal()}
                    disabled={!enableExport}
                  >
                    {t('export')}
                  </UI.Button>
                </UI.TooltipContainer>
              </ProtectedSection>
            )}
          </UI.FlexContainer>
        </UI.GridContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
          <UI.Div>
            <RegistrationFilters
              filters={filters}
              onChange={handleFiltersChange}
              onStatsChange={handleStatsChange}
              key={filtersKey}
            />
          </UI.Div>

          <UI.Div>
            <UI.GridContainer sc={{ gutter: 0.5, loading }}>
              {registrations.data.map((registration) => (
                <RegistrationCard
                  registration={registration}
                  showEvent={showEvent}
                  key={registration.id}
                />
              ))}

              {registrations.data.length === 0 && (
                <>
                  {hasFilters && (
                    <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                      <UI.Div>
                        <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                      </UI.Div>
                      {t('no_registrations_match_filters')}
                      {' '}
                      <UI.Link to={`/${project.id}/participants`}>{t('reset_filters')}</UI.Link>
                    </UI.Card>
                  )}

                  {!hasFilters && !loading && (
                    <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                      <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                      <UI.H3 sc={{ mb: 2 }}>{t('no_registrations_yet')}</UI.H3>
                      <UI.Paragraph sc={{ mb: 0 }}>
                        {t('no_registrations_get_started')}
                      </UI.Paragraph>
                    </UI.Card>
                  )}
                </>
              )}

              {(page > 1 || registrations.has_more_pages) && (
                <UI.Div sc={{ mt: 4 }}>
                  <UI.Pager disableNext={!registrations.has_more_pages} scroll />
                </UI.Div>
              )}
            </UI.GridContainer>
          </UI.Div>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Section>
  );
};

const RegistrationCard = ({
  registration,
  showEvent = false,
}: {
  registration: Registration;
  showEvent?: boolean;
}) => {
  const { t } = useTranslation();
  const project = useProject();
  const { formatDate, parseDate } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();

  const { participant } = registration;
  const to = `/${project.id}/participants/${participant.id}?${encodeUrl({ event: registration.event.id, search: `ATL${registration.sequence}` })}`;

  const [hover, setHover] = useState<boolean>();

  return (
    <UI.NavCard to={to} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <UI.GridContainer sc={{ columns: ['1fr', '1fr fit-content(120px)'], gutter: 0.5 }}>
        <UI.Div>
          <UI.H4>
            <UI.Span
              sc={{
                muted: !registration.purchase.active,
                deleted: registration.status === RegistrationStatus.cancelled,
                mr: 1,
              }}
            >
              {registration.assigned
                ? registration.full_name
                : (registration.invited_email ? t('invited') : t('not_assigned_yet'))}
            </UI.Span>
            {' '}
            <UI.Span sc={{ fontWeight: 400, muted: true }}>
              {(!registration.assigned && registration.invited_email) || registration.participant.email}
            </UI.Span>
          </UI.H4>
          <UI.FlexContainer sc={{ wrap: 'wrap' }}>
            {showEvent && !hover && (
              <UI.Label sc={{ fontWeight: 500, small: true, mt: 1 }}>
                <UI.Icon>
                  <Calendar />
                </UI.Icon>
                {' '}
                {registration.event.title}
              </UI.Label>
            )}
            {(!showEvent || hover) && (
              <UI.FadeIn sc={{ when: showEvent && hover, delay: false, flex: { wrap: 'wrap' } }}>
                <TicketLabel
                  ticket={registration.ticket.title}
                  status={registration.status}
                  sc={{ mt: 1, mr: 1 }}
                />
                {registration.status === RegistrationStatus.waitlist && (
                  <UI.Label sc={{ small: true, mt: 1, mr: 1 }}>
                    <UI.Icon>
                      <UI.Icons.Hourglass />
                    </UI.Icon>
                    {' '}
                    {t('waitlist')}
                  </UI.Label>
                )}
                {registration.time_slot && (
                  <UI.Label sc={{ small: true, mt: 1, mr: 1 }}>
                    <UI.Icon>
                      <Clock />
                    </UI.Icon>
                    {' '}
                    {formatTimeSlot(registration.time_slot)}
                  </UI.Label>
                )}
                {registration.ticket_number && (
                  <UI.Label sc={{ small: true, mt: 1 }}>
                    <UI.Icon>
                      <UI.Icons.StartFinish />
                    </UI.Icon>
                    {' '}
                    {registration.ticket_number}
                  </UI.Label>
                )}
              </UI.FadeIn>
            )}
          </UI.FlexContainer>
        </UI.Div>
        <UI.Div sc={{ small: true, textAlign: ['left', 'right'], mt: 0.5 }}>
          <UI.Div sc={{ fontWeight: 500 }}>
            ATL
            {registration.sequence}
          </UI.Div>
          <UI.Div sc={{ mt: 0.5, muted: true }}>
            {formatDate(
              parseDate(registration.created_at, { timezone: 'UTC' }),
              { format: 'display_compact_date' },
            )}
          </UI.Div>
        </UI.Div>
      </UI.GridContainer>
    </UI.NavCard>
  );
};

export default RegistrationsPage;
